import { Controller } from "@hotwired/stimulus"

const DISMISS_STORAGE_KEY = "document_import_separate_confirm_dismissed"
const MAX_SIZE = 10 * 1024 * 1024
const WARNING_SIZE = 5 * 1024 * 1024

// Dropzone for document import: drag highlight, file preview, multi-page hint
export default class extends Controller {
  static targets = [
    "dropzone",
    "fileInput",
    "fileList",
    "multipagePrompt",
    "submitButton",
    "form",
    "warning",
    "warningMessage",
    "confirmDialog",
    "confirmFileCount",
    "dismissCheckbox"
  ]
  static classes = ["dragging"]

  connect() {
    this.files = []
    this.refreshUI()
  }

  openFilePicker(event) {
    if (event.target.closest("a, button, input, label, dialog")) return
    this.fileInputTarget.click()
  }

  onDragEnter(event) {
    event.preventDefault()
    this.dragCounter = (this.dragCounter || 0) + 1
    this.dropzoneTarget.classList.add(...this.draggingClasses)
  }

  onDragLeave(event) {
    event.preventDefault()
    this.dragCounter = Math.max(0, (this.dragCounter || 0) - 1)
    if (this.dragCounter === 0) {
      this.dropzoneTarget.classList.remove(...this.draggingClasses)
    }
  }

  onDragOver(event) {
    event.preventDefault()
  }

  onDrop(event) {
    event.preventDefault()
    this.dragCounter = 0
    this.dropzoneTarget.classList.remove(...this.draggingClasses)

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    this.appendFiles(files)
    this.syncInput()
  }

  filesChanged() {
    this.appendFiles(Array.from(this.fileInputTarget.files))
    this.syncInput()
  }

  appendFiles(newFiles) {
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"]

    newFiles.forEach((file) => {
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|jpe?g|png)$/i)) return

      const duplicate = this.files.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified
      )
      if (!duplicate) this.files.push(file)
    })
  }

  syncInput() {
    const dataTransfer = new DataTransfer()
    this.files.forEach((file) => dataTransfer.items.add(file))
    this.fileInputTarget.files = dataTransfer.files
    this.refreshUI()
  }

  refreshUI() {
    this.renderFileList()
    this.updateMultipagePrompt()
    this.updateSubmitState()
  }

  handleSubmit(event) {
    if (this.oversizeFiles().length > 0) {
      event.preventDefault()
      return
    }

    if (this.confirmedMultiImport) {
      this.confirmedMultiImport = false
      return
    }

    if (this.files.length <= 1) return
    if (localStorage.getItem(DISMISS_STORAGE_KEY) === "1") return

    event.preventDefault()
    if (this.hasConfirmFileCountTarget) {
      this.confirmFileCountTarget.textContent = String(this.files.length)
    }
    if (this.hasConfirmDialogTarget) {
      this.confirmDialogTarget.showModal()
    }
  }

  confirmImport(event) {
    event.preventDefault()
    if (this.hasDismissCheckboxTarget && this.dismissCheckboxTarget.checked) {
      localStorage.setItem(DISMISS_STORAGE_KEY, "1")
    }
    if (this.hasConfirmDialogTarget) {
      this.confirmDialogTarget.close()
    }

    this.syncInput()
    this.confirmedMultiImport = true

    if (this.hasSubmitButtonTarget) {
      this.formTarget.requestSubmit(this.submitButtonTarget)
    } else {
      this.formTarget.requestSubmit()
    }
  }

  cancelImport(event) {
    event.preventDefault()
    if (this.hasConfirmDialogTarget) {
      this.confirmDialogTarget.close()
    }
  }

  removeFile(event) {
    event.preventDefault()
    event.stopPropagation()
    const index = Number.parseInt(event.params.index, 10)
    if (Number.isNaN(index) || index < 0 || index >= this.files.length) return

    this.files.splice(index, 1)
    this.syncInput()
  }

  oversizeFiles() {
    return this.files.filter((file) => file.size > MAX_SIZE)
  }

  updateSubmitState() {
    this.updateWarning()

    if (!this.hasSubmitButtonTarget) return

    const blocked = this.files.length === 0 || this.oversizeFiles().length > 0
    this.submitButtonTarget.disabled = blocked
    this.submitButtonTarget.classList.toggle("btn-disabled", blocked)
  }

  updateWarning() {
    if (!this.hasWarningTarget) return

    const oversize = this.oversizeFiles()
    const large = this.files.filter(
      (file) => file.size > WARNING_SIZE && file.size <= MAX_SIZE
    )

    if (oversize.length > 0) {
      const file = oversize[0]
      this.warningTarget.classList.remove("hidden", "alert-warning")
      this.warningTarget.classList.add("alert-error")
      this.setWarningMessage(
        `Fichier trop volumineux : ${file.name} (${this.formatSize(file.size)}). Maximum autorisé : 10 Mo.`
      )
    } else if (large.length > 0) {
      const file = large[0]
      this.warningTarget.classList.remove("hidden", "alert-error")
      this.warningTarget.classList.add("alert-warning")
      this.setWarningMessage(
        `Fichier volumineux détecté : ${file.name} (${this.formatSize(file.size)}). Le traitement peut être plus long.`
      )
    } else {
      this.warningTarget.classList.add("hidden")
    }
  }

  setWarningMessage(text) {
    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.textContent = text
    }
  }

  renderFileList() {
    if (!this.hasFileListTarget) return

    if (this.files.length === 0) {
      this.fileListTarget.innerHTML = ""
      this.fileListTarget.classList.add("hidden")
      return
    }

    this.fileListTarget.classList.remove("hidden")
    this.fileListTarget.innerHTML = this.files
      .map(
        (file, index) => `
        <li class="flex items-center justify-between gap-2 text-sm py-1.5 px-3 bg-base-200/50 rounded-lg">
          <span class="truncate font-medium flex-1 min-w-0">${this.escapeHtml(file.name)}</span>
          <span class="text-xs text-base-content/60 shrink-0">${this.formatSize(file.size)}</span>
          <button type="button"
                  class="btn btn-ghost btn-xs btn-circle shrink-0"
                  aria-label="Retirer ${this.escapeHtml(file.name)}"
                  data-action="click->document-upload-zone#removeFile"
                  data-document-upload-zone-index-param="${index}">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </li>`
      )
      .join("")
  }

  updateMultipagePrompt() {
    if (!this.hasMultipagePromptTarget) return

    const imageFiles = this.files.filter((f) => f.type.startsWith("image/"))
    const hasPdf = this.files.some((f) => f.type === "application/pdf")
    const show = imageFiles.length >= 2 && !hasPdf

    this.multipagePromptTarget.classList.toggle("hidden", !show)
  }

  formatSize(bytes) {
    if (bytes === 0) return "0 o"
    const k = 1024
    const sizes = ["o", "Ko", "Mo"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
